import {
    Text,
    Icon,
    Colors,
    formatDate,
    DATE_FORMATS,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    Spinner,
} from '@amelio/ui-library';

import type { EvaluationPeriodOption } from '@/pages/ManagerAssessment/Evaluation/types';
import type { LocalizedText } from '@/utils/localization';
import type { QueryStatus } from '@tanstack/react-query';

import { useGetLatestReviewInstanceIdByPeriod } from '@/api/review-services/review-instance';
import { ReviewType } from '@/constants/review/CycleInstance';
import { useAmelioTranslations, useNavigateInNewPage, useSelectedAppLanguage } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { RouteName } from '@/navigation/types';
import { parseServerDay, toIsoDate } from '@/utils/dateUtils';

const CUSTOM_PERIOD_VALUE = 'custom';

/**
 * The calendar day a period bound names. A predefined period carries server dates, a custom range
 * the two days the manager picked; the query wants the day either way, never the instant — read as
 * one, a server date lands on the day before anywhere west of the offset it was written in.
 */
const toCalendarDay = (bound: Date | string | undefined): string => {
    const day = typeof bound === 'string' ? parseServerDay(bound) : bound;
    return day ? toIsoDate(day) : '';
};

export interface CompetencyNote {
    id: string;
    title: string;
    content: string;
    updatedAt?: string;
    competencyName?: LocalizedText | null;
    companyValueName?: LocalizedText | null;
}

interface NotesResourcesTabProps {
    className?: string;
    dataTestId?: string;
    notes: CompetencyNote[];
    /** Where the read of the notes stands: a list still loading, or whose read failed, is not empty. */
    notesStatus: QueryStatus;
    employeeId: string;
    employeeFirstName?: string;
    selectedPeriod: string;
    periods: EvaluationPeriodOption[];
    fromDate?: Date;
    toDate?: Date;
}

interface ResourceItemProps {
    title: string;
    description: string;
    onResourceClick?: () => void;
    isDisabled?: boolean;
    tooltipMessage?: string;
}

export const NotesResourcesTab = ({
    className = '',
    dataTestId = 'notes-resources-tab',
    notes,
    notesStatus,
    employeeId,
    employeeFirstName = '',
    selectedPeriod,
    periods,
    fromDate,
    toDate,
}: NotesResourcesTabProps) => {
    const { t } = useAmelioTranslations();
    const navigateInNewPage = useNavigateInNewPage();

    const period = periods.find((option) => option.value === selectedPeriod);
    const [from, to] =
        selectedPeriod === CUSTOM_PERIOD_VALUE ? [fromDate, toDate] : [period?.from, period?.to];
    const startDate = toCalendarDay(from);
    const endDate = toCalendarDay(to);

    const { data: selfAssessmentId } = useGetLatestReviewInstanceIdByPeriod(
        employeeId,
        ReviewType.SELF_EVALUATION,
        startDate,
        endDate,
    );
    const { data: feedback360Id } = useGetLatestReviewInstanceIdByPeriod(
        employeeId,
        ReviewType.EVALUATION_360,
        startDate,
        endDate,
    );

    const notAvailableTooltip = t('errors.noEvaluationInPeriod');

    const resources: ResourceItemProps[] = [
        {
            title: t('postReviewResults.notes.selfAssessment'),
            description: t('postReviewResults.notes.selfAssessmentDescription'),
            onResourceClick: () => {
                if (startDate && endDate) {
                    navigateInNewPage({
                        path: `${RouteName.PeriodResults}/${employeeId}/${ReviewType.SELF_EVALUATION}`,
                        searchParams: { startDate, endDate },
                    });
                }
            },
            isDisabled: !selfAssessmentId,
            tooltipMessage: notAvailableTooltip,
        },
        {
            title: t('postReviewResults.notes.feedback360'),
            description: t('postReviewResults.notes.feedback360Description'),
            onResourceClick: () => {
                if (startDate && endDate) {
                    navigateInNewPage({
                        path: `${RouteName.PeriodResults}/${employeeId}/${ReviewType.EVALUATION_360}`,
                        searchParams: { startDate, endDate },
                    });
                }
            },
            isDisabled: !feedback360Id,
            tooltipMessage: notAvailableTooltip,
        },
    ];

    return (
        <div
            className={`space-y-[24px] p-[16px] sm:p-[24px] md:p-[32px] ${className}`}
            data-testid={dataTestId}
        >
            <div className={'border-b border-gray-200 pb-[32px]'}>
                <Text
                    size={'large'}
                    weight={'semibold'}
                    color={Colors.NAVY}
                    className={'text-body-lg! mb-[8px]'}
                >
                    {t('postReviewResults.notes.title')}
                </Text>

                <EvaluationNotes
                    notes={notes}
                    notesStatus={notesStatus}
                    employeeFirstName={employeeFirstName}
                />
            </div>

            <div>
                <div className={'mb-[24px]'}>
                    <Text
                        size={'large'}
                        weight={'semibold'}
                        color={Colors.NAVY}
                        className={'text-body-lg!'}
                    >
                        {t('postReviewResults.notes.preparationResources')}
                    </Text>
                    <div className={'mt-[8px]'}>
                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'text-body-sm!'}
                        >
                            {t('postReviewResults.notes.preparationDescription')}
                        </Text>
                    </div>
                </div>

                <div className={'space-y-[16px]'}>
                    {resources.map((resource) => (
                        <ResourceItem
                            key={resource.title}
                            {...resource}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

const NOTES_PLACEHOLDER_SURFACE =
    'rounded-[12px] border border-gray-200 bg-gray-50/50 p-[16px] text-center sm:p-[32px]';

interface EvaluationNotesProps {
    notes: CompetencyNote[];
    notesStatus: QueryStatus;
    employeeFirstName: string;
}

/**
 * The notes of the evaluation, or what stands in for them. Only a read that succeeded may say that
 * none were added: after a failure the notes still shown (old comments, meeting topics) are only
 * part of them, and the reader is told so.
 */
const EvaluationNotes = ({ notes, notesStatus, employeeFirstName }: EvaluationNotesProps) => {
    const { t } = useAmelioTranslations();
    const selectedLang = useSelectedAppLanguage();
    const replaceDynamicTags = useReplaceDynamicTags();

    if (notesStatus === 'pending') {
        return (
            <div
                role={'status'}
                aria-label={t('postReviewResults.notes.loading')}
                className={'flex justify-center p-6'}
            >
                <Spinner
                    size={'md'}
                    color={Colors.PRIMARY}
                />
            </div>
        );
    }

    const loadError = notesStatus === 'error' && (
        <div className={NOTES_PLACEHOLDER_SURFACE}>
            <Text
                size={'small'}
                color={Colors.ERROR}
            >
                {t('postReviewResults.notes.loadError')}
            </Text>
        </div>
    );

    if (notes.length === 0) {
        return (
            loadError || (
                <div className={NOTES_PLACEHOLDER_SURFACE}>
                    <Text
                        size={'default'}
                        color={Colors.BASE_400}
                        className={'text-body-sm!'}
                    >
                        {t('postReviewResults.notes.empty')}
                    </Text>
                </div>
            )
        );
    }

    return (
        <div className={'flex flex-col gap-[16px]'}>
            {loadError}
            {notes.map((note) => (
                <div
                    key={note.id}
                    className={'rounded-[12px] border border-gray-200 bg-white p-[16px]'}
                >
                    <Text
                        size={'small'}
                        weight={'semibold'}
                        className={'text-navy text-body-sm! mb-[4px]'}
                    >
                        {replaceDynamicTags({
                            text: note.title,
                            employee: { firstName: employeeFirstName },
                            competencyName: note.competencyName,
                            companyValueName: note.companyValueName,
                        })}
                    </Text>
                    <Text
                        size={'small'}
                        color={Colors.BASE_600}
                        className={'text-body-sm! leading-relaxed whitespace-pre-wrap'}
                    >
                        {note.content}
                    </Text>
                    {note.updatedAt && (
                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'text-caption! mt-[8px]'}
                        >
                            {formatDate({
                                date: note.updatedAt,
                                language: selectedLang,
                                format: DATE_FORMATS.DISPLAY_DATE,
                            })}
                        </Text>
                    )}
                </div>
            ))}
        </div>
    );
};

const ResourceItem = ({
    title,
    description,
    onResourceClick,
    isDisabled = false,
    tooltipMessage,
}: ResourceItemProps) => {
    const button = (
        <button
            onClick={onResourceClick}
            disabled={isDisabled}
            className={
                'focus-visible:ring-primary-800 flex h-[32px] w-[32px] items-center justify-center rounded-[4px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
                (isDisabled
                    ? 'cursor-not-allowed opacity-50 hover:bg-transparent'
                    : 'hover:bg-gray-100')
            }
            aria-label={title}
        >
            <Icon
                name={'arrow-export-2'}
                size={20}
                color={isDisabled ? Colors.BASE_400 : Colors.PRIMARY_500}
            />
        </button>
    );

    return (
        <div
            className={
                'flex items-center justify-between gap-[12px] rounded-[8px] border border-gray-200 p-[12px] transition-colors hover:bg-gray-100 sm:gap-[16px] sm:p-[24px]'
            }
        >
            <div className={'min-w-0 flex-1 cursor-pointer'}>
                <Text
                    weight={'semibold'}
                    color={Colors.NAVY}
                    className={'text-body! mb-[4px]'}
                >
                    {title}
                </Text>
                <Text
                    size={'small'}
                    color={Colors.BASE_400}
                    className={'text-body-sm! leading-relaxed'}
                >
                    {description}
                </Text>
            </div>

            {onResourceClick && (
                <div className={'flex flex-shrink-0 gap-2'}>
                    {isDisabled && tooltipMessage ? (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild={true}>
                                    <span className={'inline-flex'}>{button}</span>
                                </TooltipTrigger>
                                <TooltipContent side={'bottom'}>{tooltipMessage}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : (
                        button
                    )}
                </div>
            )}
        </div>
    );
};

export default NotesResourcesTab;
